import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { Controller, useFormContext } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import type { ConfigurationFormFieldOption } from 'src/services/configurationService';

const RESPONSE_LENGTH = {
  title: 'Response length',
  icon: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
      <path
        d="M2 2L16 2M2 6.5L16 6.5M2 11H8"
        stroke="#05050D"
        strokeWidth="2.14286"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  description: 'Control how much detail your AI provides in replies.',
};
// Keyed by the field's option keys.
const RESPONSE_LENGTH_OPTIONS: Record<
  string,
  { id: number; title: string; description?: React.ReactNode }
> = {
  concise: {
    id: 0,
    title: 'Concise 🎯',
    description: 'Short and to the point.',
  },
  standard: {
    id: 1,
    title: 'Standard ⚖️',
    description: 'Balanced responses that offer clear and informative answers.',
  },
  comprehensive: {
    id: 2,
    title: 'Comprehensive 📙',
    description:
      'Detailed responses that provide in-depth explanations, covering all relevant aspects of the query.',
  },
};

export const ResponseLengthAccordion = ({
  accordionValue,
  setAccordionValue,
  disabled = false,
  options = [],
}: {
  // The open accordion item; the three step-5 accordions share it so one is open at a time.
  accordionValue: string;
  setAccordionValue: (value: string) => void;
  disabled?: boolean;
  options?: ConfigurationFormFieldOption[];
}) => {
  const { control, watch } = useFormContext();
  const responseLength = watch('pf_tone_of_voice_resp_len');

  return (
    <div className={'flex flex-col gap-3'}>
      <div className={'flex flex-col gap-1'}>
        <div className={'flex flex-row gap-2.5 items-center'}>
          {RESPONSE_LENGTH.icon}
          <p className="text-sm font-semibold text-ebony">{RESPONSE_LENGTH.title}</p>
        </div>
        <p className={'text-sm text-ebony_opacity_50 font-medium'}>{RESPONSE_LENGTH.description}</p>
      </div>
      <div>
        {responseLength && (
          <Accordion
            value={accordionValue}
            onValueChange={setAccordionValue}
            type="single"
            className="w-full min-w-[455px] max-w-[455px]"
            collapsible={true}
            disabled={disabled}
          >
            <AccordionItem
              value="response-length-card"
              className={clsx(
                'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
                !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
              )}
            >
              <AccordionTrigger hideDefaultIcon className="py-2.5 px-5">
                <div className="flex items-center justify-between w-full">
                  <div className={'flex flex-row gap-[7px]'}>
                    <div className={'text-start'}>
                      <p className={'text-[#7A6BE5] text-sm font-semibold'}>
                        {RESPONSE_LENGTH_OPTIONS[responseLength]?.title ||
                          responseLength?.value ||
                          ''}
                      </p>
                      <p className={'text-ebony_opacity_30 text-sm font-medium'}>
                        {RESPONSE_LENGTH_OPTIONS[responseLength]?.description || ''}
                      </p>
                    </div>
                  </div>
                  {!disabled && (
                    <ExpandCollapseIcon
                      className={clsx(
                        'w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                        accordionValue === 'response-length-card' && 'rotate-180',
                      )}
                    />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-0 bg-light">
                <div className={'flex flex-col gap-2.5'}>
                  <Controller
                    control={control}
                    name="pf_tone_of_voice_resp_len"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroup
                        onValueChange={onChange}
                        value={value}
                        className="flex flex-col mt-5 mb-2.5 gap-5"
                      >
                        {options?.map((option) => {
                          const item = {
                            ...RESPONSE_LENGTH_OPTIONS[option.key],
                            ...option,
                          };

                          return (
                            <div
                              key={`${item.id}-${item.key}`}
                              className={'flex items-start gap-2.5'}
                            >
                              <RadioGroupItem
                                className={'mt-1'}
                                value={item.value}
                                id={item.value}
                              />
                              <label htmlFor={item.value} className="cursor-pointer">
                                <p className="text-sm font-semibold text-ebony opacity-80 hover:opacity-100 transition-colors">
                                  {item.title || item.key}
                                </p>
                                {item.description && (
                                  <p className={'text-sm text-ebony_opacity_50 font-medium'}>
                                    {item.description}
                                  </p>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </div>
  );
};
